import { Button, Chip, CircularProgress, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { convertUTCToLocal, parsePlan } from "../utils/StringUtil";

export const MAX_TABLE_ROWS: number = 50;

const STATUS_COLOR_MAP = {
  delivered: "dark",
  info_received: "info",
  pending: "warning",
  in_transit: "success",
  out_for_delivery: "success",
  available_for_pickup: "primary",
  failed_attempt: "warning",
  exception: "error",
};

export const ResetCreditActionCell = ({ params }: { params: any }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const { accountId, slug, recipe, planId } = params.row;

  const handleReset = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (planId !== "starter") {
      return;
    }

    const totalCredits = recipe?.total ?? 0;
    const activeCredits = recipe?.active ?? 0;

    const confirmMessage =
      `Are you sure you want to reset the credit for: ${slug}?\n\n` +
      `----------------------------------------\n` +
      `Account ID:     ${accountId}\n` +
      `Slug:           ${slug}\n` +
      `Total Credits:  ${totalCredits}\n` +
      `Active Credits: ${activeCredits}\n` +
      `----------------------------------------`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsProcessing(true);

    try {
      const resetUri = `https://fluidapps-prod.swevenbox.com/api/v1/customers/stp/transactions/${accountId}`;

      const response = await axios.delete(resetUri, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data?.status) {
        toast.success("Credits reset successfully!");
      } else {
        const errorMsg = response.data?.message || "Failed to reset credits.";
        console.warn(
          `[Reset Warning] Server returned failure response:`,
          response.data,
        );
        toast.error(errorMsg);
      }
    } catch (error: any) {
      const httpStatus = error.response?.status || "Network Error";
      console.error(
        `[Reset Error] Credit reset failed for Account: ${accountId} (${slug}). Status: ${httpStatus}`,
        error,
      );

      const fallbackMsg =
        error.response?.data?.message ||
        "An error occurred while resetting credits.";
      toast.error(fallbackMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="error"
      size="small"
      onClick={handleReset}
      disabled={planId !== "starter" || isProcessing}
      startIcon={
        isProcessing ? <CircularProgress size={16} color="inherit" /> : null
      }
      sx={{ textTransform: "none", fontWeight: 600, minWidth: "120px" }}
    >
      {isProcessing ? "Resetting..." : "Reset Credit"}
    </Button>
  );
};

export const VerifyAndSyncActionCell = ({ params }: { params: any }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [verifyStatus, setVerifyStatus] = useState<
    "idle" | "synced" | "not_synced" | "failed"
  >("idle");

  const {
    itemId,
    eventSubscriptionId,
    clientTrackerId,
    userName,
    accountId,
    userId,
  } = params.row;

  const userIdentifier = userName || accountId || "Unknown User";

  const handleVerify = async (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsVerifying(true);

    try {
      const verifyUri = `https://stp.getfluidapps.dev/api/v2/fluid/verify`;
      const response = await axios.post(
        verifyUri,
        { itemId, eventSubscriptionId, clientTrackerId },
        { headers: { "Content-Type": "application/json" } },
      );

      const data = response.data;

      if (data?.status === false || data?.success === false) {
        setVerifyStatus("failed");
        const errorMsg =
          data?.message || "Failed to verify board synchronization status.";
        console.warn(
          `[Verification Warning] Server returned failure status:`,
          data,
        );
        toast.error(errorMsg);
      } else if (data && "isEqual" in data) {
        if (data.isEqual) {
          toast.success("Board is fully updated and synchronized!");
          setVerifyStatus("synced");
        } else {
          toast.warning("Board is not synced. Updates are required.");
          setVerifyStatus("not_synced");
        }
      } else {
        setVerifyStatus("failed");
        console.warn(
          `[Verification Warning] Unexpected API response shape:`,
          data,
        );
        toast.error("Received an invalid response structure from the server.");
      }
    } catch (error: any) {
      setVerifyStatus("failed");
      console.error(
        `[Verification Error] API Call failed for Item ID: ${itemId}. Status: ${error.response?.status || "Network Error"}`,
        error,
      );
      toast.error(
        error.response?.data?.message ||
          "An error occurred during verification.",
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSync = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (
      !window.confirm(
        `Are you sure you want to sync the token for User: ${userIdentifier}?`,
      )
    ) {
      return;
    }

    setIsSyncing(true);

    try {
      const syncUri = `https://stp.getfluidapps.dev/api/v2/fluid/sync`;
      const response = await axios.post(
        syncUri,
        { itemId, eventSubscriptionId, clientTrackerId },
        { headers: { "Content-Type": "application/json" } },
      );

      const resData = response.data;

      if (resData?.errors && resData.errors.length > 0) {
        const primaryError =
          resData.errors[0]?.message || "Internal Server Error";

        console.warn(
          `[Sync Warning] Server responded with mutation errors:`,
          resData.errors,
        );
        toast.error(`Sync Failed: ${primaryError}`);
        setVerifyStatus("failed");
      } else if (resData?.data?.change_multiple_column_values?.id) {
        toast.success("Token synced successfully!");
        setVerifyStatus("synced");
      } else {
        console.warn(
          `[Sync Warning] Unexpected mutation response structure:`,
          resData,
        );
        toast.error("Failed to parse synchronization response metadata.");
        setVerifyStatus("failed");
      }
    } catch (error: any) {
      console.error(
        `[Sync Error] Network or API Call failed for Account: ${accountId}, User: ${userId || "N/A"}. Status: ${error.response?.status || "Network Error"}`,
        error,
      );

      const fallbackMsg =
        error.response?.data?.message ||
        "An error occurred while syncing token.";
      toast.error(fallbackMsg);
      setVerifyStatus("failed");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2 w-100 h-100">
      {/* 1. VERIFY BUTTON */}
      <Button
        variant="outlined"
        color="success"
        size="small"
        onClick={handleVerify}
        disabled={isVerifying || isSyncing}
        startIcon={
          isVerifying ? <CircularProgress size={16} color="inherit" /> : null
        }
        sx={{ textTransform: "none", fontWeight: 600, minWidth: "100px" }}
      >
        {isVerifying ? "Verifying..." : "Verify"}
      </Button>

      {/* 2. SYNC BUTTON (Conditional) */}
      {verifyStatus === "not_synced" && (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleSync}
          disabled={isVerifying || isSyncing}
          startIcon={
            isSyncing ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ textTransform: "none", fontWeight: 600, minWidth: "100px" }}
        >
          {isSyncing ? "Syncing..." : "Sync"}
        </Button>
      )}

      {/* 3. STATUS RESULT LABELS */}
      {verifyStatus === "synced" && (
        <Typography
          variant="caption"
          className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 ms-1"
          style={{ fontSize: "11px", fontWeight: 600 }}
        >
          Synced
        </Typography>
      )}

      {verifyStatus === "not_synced" && (
        <Typography
          variant="caption"
          className="badge bg-warning-subtle text-warning-dominant border border-warning-subtle px-2 py-1 ms-1"
          style={{ fontSize: "11px", fontWeight: 600 }}
        >
          Out of Sync
        </Typography>
      )}

      {verifyStatus === "failed" && (
        <Typography
          variant="caption"
          className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 ms-1"
          style={{ fontSize: "11px", fontWeight: 600 }}
        >
          Check Failed
        </Typography>
      )}
    </div>
  );
};

export const qritColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "dateCreated",
    type: "string",
    headerAlign: "center",
    headerName: "Date",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "referenceId",
    type: "string",
    headerAlign: "center",
    headerName: "Reference",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "accountId",
    type: "string",
    headerAlign: "center",
    headerName: "Account Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "userName",
    type: "string",
    headerAlign: "center",
    headerName: "User Name",
    sortable: true,
    width: 160,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "planId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={params.value === "free" ? "primary" : "warning"}
        sx={{ width: "100%", justifyContent: "center" }}
      />
    ),
  },
  {
    field: "actionType",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "status",
    type: "string",
    headerAlign: "center",
    headerName: "Status",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value === "completed" ? "Success" : "Failed"}
        color={params.value === "completed" ? "success" : "error"}
        sx={{ width: "100%", justifyContent: "center" }}
      />
    ),
  },
  {
    field: "note",
    type: "string",
    headerAlign: "center",
    headerName: "Remarks",
    sortable: true,
    width: 300,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value ? params.value : "No issue"}
      </div>
    ),
  },
  {
    field: "boardId",
    type: "string",
    headerAlign: "center",
    headerName: "Board ID",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "boardKind",
    type: "string",
    headerAlign: "center",
    headerName: "Board Kind",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "permissions",
    type: "string",
    headerAlign: "center",
    headerName: "Permission",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "owners",
    type: "string",
    headerAlign: "left",
    headerName: "Owners",
    sortable: true,
    width: 250,
  },
];

export const stdColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "CreatedDate",
    type: "string",
    headerAlign: "left",
    headerName: "Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "AccountId",
    type: "string",
    headerAlign: "center",
    headerName: "Account Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "AccountName",
    type: "string",
    headerAlign: "center",
    headerName: "Account Name",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "PlanId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <Chip
        label={parsePlan("smarttd", params.value)}
        color={
          parsePlan("smarttd", params.value) === "essential"
            ? "primary"
            : "warning"
        }
        sx={{ width: "100%", justifyContent: "center" }}
      />
    ),
  },
  {
    field: "BoardId",
    type: "string",
    headerAlign: "center",
    headerName: "Board ID",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "BoardType",
    type: "string",
    headerAlign: "center",
    headerName: "Board Kind",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "ActionType",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "RecipeId",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe ID",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
];

export const swnColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "CreatedDate",
    type: "string",
    headerAlign: "left",
    headerName: "Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "AccountId",
    type: "string",
    headerAlign: "center",
    headerName: "Account Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "AccountName",
    type: "string",
    headerAlign: "center",
    headerName: "Account Name",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "PlanId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <Chip
        label={parsePlan("smartwn", params.value)}
        color={
          parsePlan("smartwn", params.value) === "essential"
            ? "primary"
            : "warning"
        }
        sx={{ width: "100%", justifyContent: "center" }}
      />
    ),
  },
  {
    field: "BoardId",
    type: "string",
    headerAlign: "center",
    headerName: "Board ID",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "BoardType",
    type: "string",
    headerAlign: "center",
    headerName: "Board Kind",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "ActionType",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "RecipeId",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe ID",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
];

export const stpColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "dateCreated",
    type: "string",
    headerAlign: "center",
    headerName: "Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "referenceId",
    type: "string",
    headerAlign: "center",
    headerName: "Reference",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "accountId",
    type: "string",
    headerAlign: "center",
    headerName: "Account Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "userName",
    type: "string",
    headerAlign: "center",
    headerName: "User Name",
    sortable: true,
    width: 160,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "boardId",
    type: "string",
    headerAlign: "center",
    headerName: "Board ID",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  /* {
    field: "planId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 120,
    renderCell: (params) => {
      const colorMap = {
        starter: "info",
        basic: "success",
        plus: "secondary",
      };

      const planValue = params.value ? params.value.toLowerCase() : "";

      const chipColor = colorMap[planValue] || "default";

      return (
        <Chip
          label={params.value || "N/A"}
          color={chipColor}
          variant="outlined"
          sx={{
            width: "100%",
            justifyContent: "center",
            textTransform: "capitalize",
            fontWeight: "bold",
          }}
        />
      );
    },
  }, */
  {
    field: "actionType",
    type: "string",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    headerAlign: "center",
    width: 160,
    renderCell: (params) => {
      const status = params.value;
      const color = STATUS_COLOR_MAP[status] || "default";

      const label = status
        ? status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        : "Unknown";

      return (
        <Chip
          label={label}
          color={color}
          variant="filled"
          size="small"
          sx={{
            width: "100%",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        />
      );
    },
  },
  {
    field: "clientTrackerId",
    type: "string",
    headerAlign: "center",
    headerName: "Tracking Number",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value.split("-")[1]}
      </div>
    ),
  },
  {
    field: "postalCode",
    type: "string",
    headerAlign: "center",
    headerName: "Postal Code",
    sortable: true,
    width: 180,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "courier",
    type: "string",
    headerAlign: "left",
    headerName: "Couriers",
    sortable: true,
    width: 250,
    renderCell: (params) => {
      const couriers = Array.isArray(params.value) ? params.value : [];

      return (
        <div className="w-100 text-start align-center text-capitalize">
          {couriers.length > 0 ? couriers.join(", ") : "N/A"}
        </div>
      );
    },
  },
  /* {
    field: "note",
    type: "string",
    headerAlign: "center",
    headerName: "Remarks",
    sortable: true,
    width: 550,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  }, */
  {
    field: "boardKind",
    type: "string",
    headerAlign: "center",
    headerName: "Board Kind",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "permissions",
    type: "string",
    headerAlign: "center",
    headerName: "Permission",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "owners",
    type: "string",
    headerAlign: "left",
    headerName: "Owners",
    sortable: true,
    width: 200,
  },
  {
    field: "actions",
    headerAlign: "left",
    headerName: "Actions",
    type: "actions",
    width: 320,
    renderCell: (params: GridRenderCellParams) => (
      <VerifyAndSyncActionCell params={params} />
    ),
  },
];

export const stpColumnsError: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "createdAt",
    type: "string",
    headerAlign: "left",
    headerName: "Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "referenceId",
    type: "string",
    headerAlign: "center",
    headerName: "Reference",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "account_id",
    type: "string",
    headerAlign: "center",
    headerName: "Account Id",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "user_id",
    type: "string",
    headerAlign: "left",
    headerName: "User ID",
    sortable: true,
    width: 100,
  },
  {
    field: "AccountName",
    type: "string",
    headerAlign: "center",
    headerName: "Account Name",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "trackingNumber",
    type: "string",
    headerAlign: "center",
    headerName: "Tracking Number",
    sortable: true,
    width: 180,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "courierCode",
    type: "string",
    headerAlign: "center",
    headerName: "Courier Code",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "postalCode",
    type: "string",
    headerAlign: "center",
    headerName: "Postal Code",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "clientTrackerId",
    type: "string",
    headerAlign: "center",
    headerName: "Tracker ID",
    sortable: true,
    width: 250,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },

  {
    field: "errorCode",
    type: "string",
    headerAlign: "center",
    headerName: "Error Code",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "errorMessage",
    type: "string",
    headerAlign: "left",
    headerName: "Error Message",
    sortable: true,
    width: 350,
    renderCell: (params) => (
      <div className="w-100 text-start align-center">{params.value}</div>
    ),
  },
  {
    field: "board_id",
    type: "string",
    headerAlign: "center",
    headerName: "Board ID",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "item_id",
    type: "string",
    headerAlign: "center",
    headerName: "Item ID",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "action_type",
    type: "string",
    headerAlign: "center",
    headerName: "action_type",
    sortable: true,
    width: 100,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "updatedAt",
    type: "string",
    headerAlign: "left",
    headerName: "Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
];

// ACTIONS COLUMNS
export const qritAccountColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "installDate",
    type: "string",
    headerAlign: "center",
    headerName: "Install Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "accountId",
    type: "string",
    headerAlign: "center",
    headerName: " Account ID",
    sortable: true,
    width: 140,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "slug",
    type: "string",
    headerAlign: "center",
    headerName: "Slug",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  } /* 
    {
      field: "accountName",
      type: "string",
      headerAlign: "left",
      headerName: " Account Name",
      sortable: true,
      width: 150,
    },
    {
      field: "userId",
      type: "string",
      headerAlign: "left",
      headerName: "User Id",
      sortable: true,
      width: 100,
    }, */,
  {
    field: "userName",
    type: "string",
    headerAlign: "center",
    headerName: "User Name",
    sortable: true,
    width: 180,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "userEmail",
    type: "string",
    headerAlign: "center",
    headerName: "Email",
    sortable: true,
    width: 200,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "isTrial",
    type: "boolean",
    headerAlign: "center",
    headerName: "Trial",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "isTrialStatus",
    type: "string",
    headerAlign: "center",
    headerName: "Status",
    sortable: true,
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={
          params.value === "subscribed"
            ? "success"
            : params.value === "trial"
              ? "primary"
              : "error"
        }
        sx={{ width: "100%", justifyContent: "center" }}
      />
    ),
  },
  {
    field: "totalRecipe",
    type: "number",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "totalCreate",
    type: "number",
    headerAlign: "center",
    headerName: "Created",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScanned",
    type: "number",
    headerAlign: "center",
    headerName: "Scanned",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScannedSuccess",
    type: "number",
    headerAlign: "center",
    headerName: "Success",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScannedFailed",
    type: "number",
    headerAlign: "center",
    headerName: "Failed",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "usedCredits",
    type: "number",
    headerAlign: "center",
    headerName: "Used",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== null ? params.value : "0"}
      </div>
    ),
  },
  {
    field: "credit",
    type: "number",
    headerAlign: "center",
    headerName: "Credit",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "planId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 100,
    renderCell: (params) => {
      return (
        <Chip
          label={params.value}
          color={
            params.value === "trial"
              ? "warning"
              : params.value === "free"
                ? "success"
                : "primary"
          }
          sx={{ width: "100%", justifyContent: "center" }}
        />
      );
    },
    cellClassName: (params) => parsePlan("qrit", params.value),
  },
  {
    field: "subscriptionDate",
    type: "string",
    headerAlign: "left",
    headerName: "Subscription Date",
    sortable: true,
    width: 200,
  },
  {
    field: "renewalDate",
    type: "string",
    headerAlign: "left",
    headerName: "Renewal Date",
    sortable: true,
    width: 200,
  },
  {
    field: "appVersion",
    type: "string",
    headerAlign: "left",
    headerName: "App Version",
    sortable: true,
    width: 120,
  },
];

export const stpAccountColumns: GridColDef[] = [
  {
    field: "id",
    type: "number",
    headerAlign: "right",
    headerName: "#",
    width: 50,
  },
  {
    field: "installDate",
    type: "string",
    headerAlign: "center",
    headerName: "Install Date",
    sortable: true,
    width: 210,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {convertUTCToLocal(params.value)}
      </div>
    ),
  },
  {
    field: "accountId",
    type: "string",
    headerAlign: "center",
    headerName: " Account ID",
    sortable: true,
    width: 140,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "slug",
    type: "string",
    headerAlign: "center",
    headerName: "Slug",
    sortable: true,
    width: 150,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "userName",
    type: "string",
    headerAlign: "center",
    headerName: "User Name",
    sortable: true,
    width: 180,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "userEmail",
    type: "string",
    headerAlign: "center",
    headerName: "Email",
    sortable: true,
    width: 250,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "planId",
    type: "string",
    headerAlign: "center",
    headerName: "Plan Id",
    sortable: true,
    width: 120,
    renderCell: (params) => {
      const colorMap = {
        starter: "info",
        basic: "success",
        plus: "secondary",
      };

      const planValue = params.value ? params.value.toLowerCase() : "";

      const chipColor = colorMap[planValue] || "default";

      const outlietColor = params.value !== "starter" ? "filled" : "outlined";

      return (
        <Chip
          label={params.value || "N/A"}
          color={chipColor}
          variant={outlietColor}
          sx={{
            width: "100%",
            justifyContent: "center",
            textTransform: "capitalize",
            fontWeight: "bold",
          }}
        />
      );
    },
  },
  {
    field: "totalRecipe",
    type: "number",
    headerAlign: "center",
    headerName: "Recipe",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">{params.value}</div>
    ),
  },
  {
    field: "totalCreate",
    type: "number",
    headerAlign: "center",
    headerName: "Created",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScanned",
    type: "number",
    headerAlign: "center",
    headerName: "Scanned",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScannedSuccess",
    type: "number",
    headerAlign: "center",
    headerName: "Success",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "totalScannedFailed",
    type: "number",
    headerAlign: "center",
    headerName: "Failed",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "usedCredits",
    type: "number",
    headerAlign: "center",
    headerName: "Used",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== null ? params.value : "0"}
      </div>
    ),
  },
  {
    field: "credit",
    type: "number",
    headerAlign: "center",
    headerName: "Credit",
    sortable: true,
    width: 80,
    renderCell: (params) => (
      <div className="w-100 text-center align-center">
        {params.value !== -1 ? params.value : "∞"}
      </div>
    ),
  },
  {
    field: "subscriptionDate",
    type: "string",
    headerAlign: "left",
    headerName: "Subscription Date",
    sortable: true,
    width: 200,
  },
  {
    field: "renewalDate",
    type: "string",
    headerAlign: "left",
    headerName: "Renewal Date",
    sortable: true,
    width: 200,
  },
  {
    field: "appVersion",
    type: "string",
    headerAlign: "left",
    headerName: "App Version",
    sortable: true,
    width: 120,
  },
  {
    field: "actions",
    headerName: "Actions",
    type: "actions",
    width: 150,
    renderCell: (params: GridRenderCellParams) => (
      <ResetCreditActionCell params={params} />
    ),
  },
];
